import type { ColumnDefinition } from "@frui.ts/dataviews";
import type { ViewComponent } from "@frui.ts/views";
import { registerView } from "@frui.ts/views";
import type UnpairedPayment from "entities/unpairedPayment";
import { observer } from "mobx-react-lite";
import React from "react";
import { Button, ButtonToolbar } from "react-bootstrap";
import { formatAmount, formatDate } from "utils/localizations";
import type PairingByOrderPaymentsListViewModel from "viewModels/finance/paymentsPairing/pairingByOrder/pairingByOrderPaymentsListViewModel";
import PairingByPaymentListViewModel from "viewModels/finance/paymentsPairing/pairingByPayment/pairingByPaymentListViewModel";
import ListView from "views/common/list";
import UnpairedPaymentStatus from "entities/unpairedPaymentStatus";
import PaymentFilters from "../paymentFilters";
import { formatBankAccount } from "@edaz/utils/bankAccount";
import { emptyValue } from "utils/emptyValue";

export function unpairedPaymentsColumns(
  vm: PairingByPaymentListViewModel | PairingByOrderPaymentsListViewModel
): ColumnDefinition<UnpairedPayment>[] {
  return [
    {
      property: "senderBankAccount",
      title: vm.translateAttribute("senderBankAccount"),
      valueFormatter: props => formatBankAccount(props.value),
      cellProps: props => ({ style: { whiteSpace: "pre-line" } }),
    },
    {
      property: "variableSymbol",
      title: vm.translateAttribute("variableSymbol").replace("symbol", "s."),
      valueFormatter: props => props.value || emptyValue,
    },
    {
      property: "constantSymbol",
      title: vm.translateAttribute("constantSymbol").replace("symbol", "s."),
      valueFormatter: props => props.value || emptyValue,
    },
    {
      property: "specificSymbol",
      title: vm.translateAttribute("specificSymbol").replace("symbol", "s."),
      valueFormatter: props => props.value || emptyValue,
    },
    {
      property: "paidAmount",
      title: vm.translateAttribute("paidAmount"),
      valueFormatter: props => formatAmount(props.value),
    },
    {
      property: "postingDate",
      title: vm.translateAttribute("postingDate"),
      valueFormatter: props => formatDate(props.value),
    },
    {
      property: "messageForRecipient",
      title: vm.translateAttribute("messageForRecipient"),
      valueFormatter: props => props.value || emptyValue,
    },
  ];
}

const PairingByPaymentListView: ViewComponent<PairingByPaymentListViewModel> = observer(({ vm }) => {
  const onItemClick = vm.onItemClick();
  const columns: ColumnDefinition<UnpairedPayment>[] = [
    ...unpairedPaymentsColumns(vm),
    {
      property: "status",
      title: vm.translateAttribute("status"),
      valueFormatter: props => {
        if (props.item.isPaired) {
          return <span className="font-weight-bold text-danger">{vm.translate("alreadyPaired")}</span>;
        } else if (props.value === UnpairedPaymentStatus.RefundCreated) {
          return <span className="font-weight-bold text-success">{vm.translate("refundCreated")}</span>;
        } else if (props.value === UnpairedPaymentStatus.New) {
          if (props.item.isRefundable && vm.allowToRefund) {
            return (
              <Button variant="primary" className="rounded" size="sm" onClick={() => vm.onCreateRefundClick(props.item)}>
                {vm.translate("createRefund")}
              </Button>
            );
          } else if (props.item.isRefundable && !vm.allowToRefund) {
            return <span className="font-weight-bold text-success">{vm.translate("refundToCreated")}</span>;
          } else {
            return <span className="font-weight-bold text-danger">{vm.translate("missingInformation")}</span>;
          }
        } else {
          return emptyValue;
        }
      },
    },
    {
      cellClassName: "sticky",
      headerClassName: "sticky",
      valueFormatter: props => {
        if (props.item.isPaired) {
          return null;
        }

        return (
          <ButtonToolbar className="justify-content-end">
            <Button
              className="rounded"
              variant="success"
              size="sm"
              onClick={onItemClick ? () => onItemClick(props.item) : undefined}>
              {vm.translate(props.item.isPairable && vm.allowToPair ? "match" : "paymentDetail")}
            </Button>
          </ButtonToolbar>
        );
      },
    },
  ];

  return (
    <>
      {!vm.activeChild && <PaymentFilters vm={vm} />}
      <ListView
        vm={vm}
        columns={columns}
        hideTitle={true}
        hideButtons={true}
        rowProps={item => {
          if (item.isPaired) {
            return {
              className: "text-danger",
            };
          }

          return {};
        }}
      />
    </>
  );
});

registerView(PairingByPaymentListView, PairingByPaymentListViewModel);
